import type { CSSProperties, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { match } from 'ts-pattern';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { TextLink } from '@interxfi/design-system/components/link';
import { BaseLink } from '@interxfi/design-system/components/link/base-link';
import { Logomark, type LogomarkSize } from '@interxfi/design-system/components/logomark/logomark';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import type { TypographyProps } from '@interxfi/design-system/components/typography';
import { Typography } from '@interxfi/design-system/components/typography';
import { useProgramArtifact } from '../artifact/use-program-artifact';
import { getProgramDetailPath } from '../program-routing';
import styles from './program-logomark.module.scss';

const truncatedStyle = { flex: '1 1 auto', minWidth: 0 } as const satisfies CSSProperties;
const baseTypography = {
  lineHeight: '1rem',
  size: '0.875rem',
  tone: 'neutral-dk',
} as const satisfies TypographyProps;

const truncatedContainerStyles = {
  width: '100%',
} as const satisfies CSSProperties;
export interface ProgramLogomarkProps {
  className?: string;
  canUsePendingLogoMark?: boolean;
  hideUntilLoaded?: boolean;
  nameTypography?: TypographyProps;
  overrideSrc?: string | null;
  programData?: { code: string | undefined; name: string | undefined; logoMarkArtifactId: string | undefined };
  programId?: string;
  shouldLoadLogoMark?: boolean;
  size?: LogomarkSize;
  withCode?: boolean;
  withLink?: boolean;
  withName?: boolean;
  withTooltip?: boolean;
}

export function ProgramLogomark({
  canUsePendingLogoMark,
  className,
  hideUntilLoaded,
  nameTypography,
  overrideSrc,
  programData,
  programId,
  shouldLoadLogoMark,
  size,
  withCode,
  withLink = true,
  withName,
  withTooltip,
}: ProgramLogomarkProps) {
  const { data } = useGetProgram(!programData && programId ? { programId } : undefined);
  const name = programData?.name || data?.program?.data?.details?.name;
  const code = programData?.code || data?.program?.programCode;
  const renderedName = [name, withCode && code ? (name ? `(${code})` : code) : undefined].filter(Boolean).join(' ');
  const logomarkArtifactId = match({ programData, canUsePendingLogoMark, data })
    .with(
      { canUsePendingLogoMark: true },
      (d) =>
        d.programData?.logoMarkArtifactId ||
        d.data?.program?.data?.currentBrandingVersion?.branding?.logoMarkArtifactId ||
        d.data?.program?.data?.pendingBrandingVersion?.branding?.logoMarkArtifactId,
    )
    .otherwise((d) => d.programData?.logoMarkArtifactId || d.data?.program?.data?.currentBrandingVersion?.branding?.logoMarkArtifactId);
  const logomarkSrc = useProgramArtifact(programId, logomarkArtifactId, !shouldLoadLogoMark);
  const shouldTruncateLabel = nameTypography?.wrap === 'ellipsis';
  const typographyProps = shouldTruncateLabel
    ? { ...baseTypography, ...nameTypography, style: { ...nameTypography?.style, ...truncatedStyle } }
    : { ...baseTypography, ...nameTypography };

  if (hideUntilLoaded && !programData && !data?.program) {
    return null;
  }

  let baseLogomark = (
    <Logomark
      className={className}
      fileSrc={overrideSrc !== undefined ? overrideSrc || undefined : logomarkSrc?.file}
      size={size}
      tenantName={renderedName}
      tenantType="program"
    />
  );

  let baseLabel: ReactNode = withName && renderedName ? renderedName : null;

  const linkTo = getProgramDetailPath(programId);

  if (programId && withLink && linkTo) {
    baseLogomark = (
      <BaseLink aria-label={`Program ${renderedName || programId}`} className={styles.link} LinkElement={Link} to={linkTo}>
        {baseLogomark}
      </BaseLink>
    );

    if (baseLabel) {
      baseLabel =
        !nameTypography?.decoration || nameTypography.decoration === 'underline' ? (
          <TextLink LinkElement={Link} to={linkTo} typography={typographyProps}>
            {baseLabel}
          </TextLink>
        ) : (
          <BaseLink className={clsx(styles.link, styles.nameLink)} LinkElement={Link} to={linkTo}>
            <Typography {...typographyProps}>{baseLabel}</Typography>
          </BaseLink>
        );
    }
  } else {
    baseLabel = baseLabel ? <Typography {...typographyProps}>{baseLabel}</Typography> : null;
  }

  return (
    <HorizontalStack blockAlign="center" gap={2} style={shouldTruncateLabel ? truncatedContainerStyles : undefined} wrap="nowrap">
      {withTooltip ? <Tooltip activator={baseLogomark}>{renderedName}</Tooltip> : baseLogomark}

      {baseLabel}
    </HorizontalStack>
  );
}
